import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, List, Tag, Progress, Spin, Empty, Button, Alert, Space, Badge } from 'antd';
import {
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Project, GateSummary, ChecklistItem } from '../types';
import { useAuth } from '../context/AuthContext';
import { useLookups } from '../context/LookupsContext';

const { Title, Text } = Typography;

interface AttentionItem extends ChecklistItem {
  projectName: string;
  projectId: string;
  checklistId: string;
}

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState<Map<string, GateSummary[]>>(new Map());
  const [myItems, setMyItems] = useState<AttentionItem[]>([]);
  const [approvalItems, setApprovalItems] = useState<AttentionItem[]>([]);
  const { user } = useAuth();
  const { getStatusName } = useLookups();
  const navigate = useNavigate();

  const userEmail = user?.email;

  useEffect(() => {
    if (userEmail) {
      loadData();
    }
  }, [userEmail]);

  const loadData = async () => {
    if (!userEmail) return;

    setLoading(true);
    try {
      const projectsData = await api.getProjects('active');
      setProjects(projectsData);

      // Load gate summaries and attention items
      const statsMap = new Map<string, GateSummary[]>();
      const myItemsList: AttentionItem[] = [];
      const approvalItemsList: AttentionItem[] = [];

      for (const project of projectsData) {
        if (project.checklists && project.checklists.length > 0) {
          // Get user's teams for this specific project
          const userTeamsInProject = (project.teamAssignments || [])
            .filter(a => a.email === userEmail)
            .map(a => a.teamCode);

          // Skip project if user isn't assigned to any teams
          if (userTeamsInProject.length === 0) continue;

          for (const checklist of project.checklists) {
            try {
              const summary = await api.getGateSummary(checklist.id);
              statsMap.set(project.id, summary);

              // Get items for attention section
              const items = await api.getChecklistItems(checklist.id);

              items.forEach((item) => {
                const receiverTeams = item.templateItem?.receiverTeams || [];
                const approverTeams = item.templateItem?.approverTeams || [];

                // Check if any of user's teams in this project match receiver/approver
                const isReceiver = receiverTeams.some(t => userTeamsInProject.includes(t));
                const isApprover = approverTeams.some(t => userTeamsInProject.includes(t));

                // My items: where user's team is receiver and not completed
                if (isReceiver && item.status !== 'approved' && !item.isNa) {
                  myItemsList.push({
                    ...item,
                    projectName: project.name,
                    projectId: project.id,
                    checklistId: checklist.id,
                  });
                }

                // Approval items: where user's team is approver and status is 'resolved'
                if (isApprover && item.status === 'resolved') {
                  approvalItemsList.push({
                    ...item,
                    projectName: project.name,
                    projectId: project.id,
                    checklistId: checklist.id,
                  });
                }
              });
            } catch (err) {
              console.error(`Failed to load data for checklist ${checklist.id}:`, err);
            }
          }
        }
      }
      setProjectStats(statsMap);

      // Sort by overdue first, then by priority
      const sortItems = (a: AttentionItem, b: AttentionItem) => {
        if (a.isOverdue && !b.isOverdue) return -1;
        if (!a.isOverdue && b.isOverdue) return 1;
        const priorityOrder: Record<string, number> = { high: 0, med: 1, low: 2 };
        return (priorityOrder[a.priority || 'med'] ?? 1) - (priorityOrder[b.priority || 'med'] ?? 1);
      };

      setMyItems(myItemsList.sort(sortItems).slice(0, 5));
      setApprovalItems(approvalItemsList.sort(sortItems).slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOverallProgress = (summary: GateSummary[]): number => {
    if (!summary || summary.length === 0) return 0;
    const total = summary.reduce((acc, gate) => acc + gate.total, 0);
    const completed = summary.reduce((acc, gate) => acc + gate.completed + gate.naCount, 0);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getTotalStats = () => {
    let totalItems = 0;
    let completedItems = 0;
    let inProgressItems = 0;

    projectStats.forEach((summary) => {
      summary.forEach((gate) => {
        totalItems += gate.total;
        completedItems += gate.completed + gate.naCount;
        inProgressItems += gate.inProgress;
      });
    });

    return { totalItems, completedItems, inProgressItems };
  };

  const getStatusTagColor = (status: string): string => {
    switch (status) {
      case 'approved': return 'success';
      case 'resolved': return 'processing';
      case 'received': return 'cyan';
      case 'in_progress': return 'warning';
      case 'requested': return 'orange';
      default: return 'default';
    }
  };

  const getPriorityTagColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'red';
      case 'med': return 'orange';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const stats = getTotalStats();
  const overdueCount = myItems.filter(i => i.isOverdue).length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={3}>Welcome back, {user?.name}</Title>
      <Text type="secondary">Here's your due diligence overview</Text>

      {/* Attention Alerts */}
      {(overdueCount > 0 || approvalItems.length > 0) && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {overdueCount > 0 && (
            <Col span={24}>
              <Alert
                message={`${overdueCount} item${overdueCount > 1 ? 's' : ''} overdue`}
                description="You have items that need immediate attention"
                type="error"
                showIcon
                icon={<WarningOutlined />}
                action={
                  <Button size="small" danger onClick={() => navigate('/my-items')}>
                    View Items
                  </Button>
                }
              />
            </Col>
          )}
          {approvalItems.length > 0 && (
            <Col span={24}>
              <Alert
                message={`${approvalItems.length} item${approvalItems.length > 1 ? 's' : ''} pending your approval`}
                type="info"
                showIcon
                icon={<SafetyCertificateOutlined />}
                action={
                  <Button size="small" type="primary" onClick={() => navigate('/needs-approval')}>
                    Review
                  </Button>
                }
              />
            </Col>
          )}
        </Row>
      )}

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Projects"
              value={projects.length}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#1c7a92' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Completed Items"
              value={stats.completedItems}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="In Progress"
              value={stats.inProgressItems}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Review"
              value={stats.totalItems - stats.completedItems - stats.inProgressItems}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Access Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* My Items */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>My Items</span>
                <Badge count={myItems.length} style={{ backgroundColor: '#1c7a92' }} />
              </Space>
            }
            extra={
              <Button type="link" onClick={() => navigate('/my-items')}>
                View All <RightOutlined />
              </Button>
            }
          >
            {myItems.length === 0 ? (
              <Empty description="No items assigned to you" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                dataSource={myItems}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/projects/${item.projectId}/checklist/${item.checklistId}`)}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          {item.isOverdue && <WarningOutlined style={{ color: '#ff4d4f' }} />}
                          <Text ellipsis style={{ maxWidth: 250 }}>{item.templateItem?.itemText}</Text>
                        </Space>
                      }
                      description={
                        <Space size={4}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{item.projectName}</Text>
                          <Tag color={getStatusTagColor(item.status)} style={{ margin: 0 }}>
                            {getStatusName(item.status)}
                          </Tag>
                          {item.priority && (
                            <Tag color={getPriorityTagColor(item.priority)} style={{ margin: 0 }}>
                              {item.priority.toUpperCase()}
                            </Tag>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Needs Approval */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <SafetyCertificateOutlined />
                <span>Needs Approval</span>
                <Badge count={approvalItems.length} style={{ backgroundColor: '#52c41a' }} />
              </Space>
            }
            extra={
              <Button type="link" onClick={() => navigate('/needs-approval')}>
                View All <RightOutlined />
              </Button>
            }
          >
            {approvalItems.length === 0 ? (
              <Empty description="No items pending approval" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                dataSource={approvalItems}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/projects/${item.projectId}/checklist/${item.checklistId}`)}
                    actions={[
                      <Button
                        type="primary"
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.updateItemStatus(item.id, 'approved');
                            loadData();
                          } catch (err) {
                            console.error('Failed to approve:', err);
                          }
                        }}
                      >
                        Approve
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={<Text ellipsis style={{ maxWidth: 200 }}>{item.templateItem?.itemText}</Text>}
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.projectName}</Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Projects List */}
      <Card title="Active Projects" style={{ marginTop: 24 }}>
        {projects.length === 0 ? (
          <Empty description="No active projects">
            <Button type="primary" onClick={() => navigate('/projects')}>
              Create Project
            </Button>
          </Empty>
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={projects}
            renderItem={(project) => {
              const summary = projectStats.get(project.id);
              const progress = getOverallProgress(summary || []);
              const checklist = project.checklists?.[0];

              return (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      onClick={() =>
                        checklist
                          ? navigate(`/projects/${project.id}/checklist/${checklist.id}`)
                          : navigate(`/projects/${project.id}`)
                      }
                    >
                      View Details
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        {project.name}
                      </span>
                    }
                    description={
                      <span>
                        {project.city && project.state && `${project.city}, ${project.state} • `}
                        {checklist ? (
                          <Tag color="blue">Gate {checklist.currentGate}</Tag>
                        ) : (
                          <Tag>No Checklist</Tag>
                        )}
                      </span>
                    }
                  />
                  <div style={{ width: 200 }}>
                    <Progress
                      percent={progress}
                      size="small"
                      status={progress === 100 ? 'success' : 'active'}
                    />
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
